import { forwardRef, useState, useMemo, useEffect } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import Slide from '@material-ui/core/Slide'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Checkbox from '@material-ui/core/Checkbox'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useMediaQuery } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Close from '@material-ui/icons/Close'
import AssignmentInd from '@material-ui/icons/AssignmentInd'

import { useTheme, makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
	sectionTitle: {
		fontWeight: 'bold',
		fontSize: '0.95rem',
		marginBottom: theme.spacing(1),
		color: '#333',
	},
	tableContainer: {
		maxHeight: 300,
		border: '1px solid #e0e0e0',
	},
	tableHeadCell: {
		fontWeight: 'bold',
		backgroundColor: '#f5f5f5',
		position: 'sticky',
		top: 0,
		zIndex: 1,
	},
	previewContainer: {
		maxHeight: 200,
		overflowY: 'auto',
		border: '1px solid #e0e0e0',
		borderRadius: 4,
		padding: theme.spacing(1),
		backgroundColor: '#fafafa',
	},
	previewRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: '4px 8px',
		'&:nth-child(even)': {
			backgroundColor: '#f0f0f0',
		},
	},
	summaryChip: {
		margin: theme.spacing(0.5),
	},
}))

function getAdminLabel(admin) {
	return admin.name ? admin.name : `${admin.firstname} ${admin.lastname}`
}

export default function BulkAssignAdminModal({
	open,
	onClose,
	handleButton,
	checkerAdminList,
	assignableRows = [],
	isLoadingAssignableRows = false,
}) {
	const theme = useTheme()
	const classes = useStyles()
	const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

	const [selectedSDocIDs, setSelectedSDocIDs] = useState([])
	const [selectedAdmins, setSelectedAdmins] = useState([])

	useEffect(() => {
		if (!open) {
			setSelectedSDocIDs([])
			setSelectedAdmins([])
		}
	}, [open])

	useEffect(() => {
		setSelectedSDocIDs(prev =>
			prev.filter(id => assignableRows.some(row => row.id === id))
		)
	}, [assignableRows])

	const handleSelectAll = (event) => {
		if (event.target.checked) {
			setSelectedSDocIDs(assignableRows.map(row => row.id))
		} else {
			setSelectedSDocIDs([])
		}
	}

	const handleToggleSDoc = (id) => {
		setSelectedSDocIDs(prev =>
			prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
		)
	}

	const assignments = useMemo(() => {
		if (selectedSDocIDs.length === 0 || selectedAdmins.length === 0) return []
		return selectedSDocIDs.map((sDocID, index) => {
			const admin = selectedAdmins[index % selectedAdmins.length]
			const row = assignableRows.find(r => r.id === sDocID)
			return {
				sDocID,
				adminID: admin.id,
				adminName: getAdminLabel(admin),
				runningNumber: row?.runningNumber || '-',
				brandName: row?.brandName || '-',
			}
		})
	}, [selectedSDocIDs, selectedAdmins, assignableRows])

	const summary = useMemo(() => {
		const counts = {}
		assignments.forEach(a => {
			counts[a.adminName] = (counts[a.adminName] || 0) + 1
		})
		return Object.entries(counts)
	}, [assignments])

	const handleConfirm = () => {
		const payload = assignments.map(a => ({
			sDocID: a.sDocID,
			adminID: a.adminID,
		}))
		handleButton(payload)
	}

	const handleClose = () => {
		setSelectedSDocIDs([])
		setSelectedAdmins([])
		onClose()
	}

	const isAllSelected = assignableRows.length > 0 && selectedSDocIDs.length === assignableRows.length

	return (
		<Dialog
			maxWidth="md"
			fullWidth
			fullScreen={fullScreen}
			onClose={handleClose}
			open={open}
			TransitionComponent={Transition}>
			<DialogTitle style={{ backgroundColor: '#a74345' }}>
				<Grid
					container
					direction="row"
					justifyContent="space-between"
					alignItems="center">
					<Grid className="flex flex-row items-center" component="div">
						<AssignmentInd style={{ color: '#fff', marginRight: 8 }} />
						<Grid className="text-white text-lg">มอบหมายงานแบบกลุ่ม</Grid>
					</Grid>
					<IconButton onClick={handleClose} style={{ color: '#fff' }}>
						<Close />
					</IconButton>
				</Grid>
			</DialogTitle>
			<DialogContent dividers>
				<Grid container spacing={2}>
					{/* Section 1: Select Officers */}
					<Grid item xs={12}>
						<Typography className={classes.sectionTitle}>
							1. เลือกผู้ตรวจสอบข้อมูล
						</Typography>
						<Autocomplete
							multiple
							options={checkerAdminList}
							getOptionLabel={getAdminLabel}
							getOptionSelected={(option, value) => option.id === value.id}
							value={selectedAdmins}
							onChange={(_event, newValue) => setSelectedAdmins(newValue)}
							noOptionsText="ไม่พบรายชื่อที่ค้นหา"
							renderInput={(params) => (
								<TextField
									{...params}
									label="ค้นหาและเลือกผู้ตรวจสอบ (เลือกได้หลายคน)"
									variant="outlined"
									placeholder="พิมพ์ชื่อเพื่อค้นหา..."
									size="small"
								/>
							)}
						/>
						{selectedAdmins.length > 0 && (
							<Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
								เลือกแล้ว {selectedAdmins.length} คน — งานจะถูกกระจายแบบวนรอบ (Round-Robin)
							</Typography>
						)}
					</Grid>

					<Grid item xs={12}>
						<Divider />
					</Grid>

					{/* Section 2: Select SDocs */}
					<Grid item xs={12}>
						<Grid container justifyContent="space-between" alignItems="center">
							<Typography className={classes.sectionTitle}>
								2. เลือกรายการที่ต้องการมอบหมาย
							</Typography>
							<Typography variant="body2" color="textSecondary">
								เลือกแล้ว {selectedSDocIDs.length} / {assignableRows.length} รายการ
							</Typography>
						</Grid>
						{isLoadingAssignableRows ? (
							<Grid className="flex flex-col items-center justify-center" style={{ padding: 24 }}>
								<CircularProgress size={28} />
								<Typography variant="body2" color="textSecondary" style={{ marginTop: 12, display: 'none' }}>
									กำลังโหลดรายการที่สามารถมอบหมายได้...
								</Typography>
							</Grid>
						) : assignableRows.length === 0 ? (
							<Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
								ไม่มีรายการที่สามารถมอบหมายได้ในระบบ
							</Typography>
						) : (
							<TableContainer component={Paper} className={classes.tableContainer}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell padding="checkbox" className={classes.tableHeadCell}>
												<Checkbox
													indeterminate={selectedSDocIDs.length > 0 && selectedSDocIDs.length < assignableRows.length}
													checked={isAllSelected}
													onChange={handleSelectAll}
													color="primary"
												/>
											</TableCell>
											<TableCell className={classes.tableHeadCell}>เลขที่คำขอ</TableCell>
											<TableCell className={classes.tableHeadCell}>ตราอักษร</TableCell>
											<TableCell className={classes.tableHeadCell}>รุ่นผลิตภัณฑ์</TableCell>
											<TableCell className={classes.tableHeadCell}>ผู้ยื่น</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{assignableRows.map(row => (
											<TableRow
												key={row.id}
												hover
												onClick={() => handleToggleSDoc(row.id)}
												style={{ cursor: 'pointer' }}>
												<TableCell padding="checkbox">
													<Checkbox
														checked={selectedSDocIDs.includes(row.id)}
														color="primary"
													/>
												</TableCell>
												<TableCell>{row.runningNumber}</TableCell>
												<TableCell>{row.brandName}</TableCell>
												<TableCell>{row.productModel}</TableCell>
												<TableCell>{row.operator}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Grid>

					{/* Section 3: Preview */}
					{assignments.length > 0 && (
						<Grid item xs={12}>
							<Divider style={{ marginBottom: 8 }} />
							<Typography className={classes.sectionTitle}>
								3. ตัวอย่างการมอบหมาย (Round-Robin)
							</Typography>

							{/* Summary chips */}
							<Grid className="flex flex-row flex-wrap mb-2">
								{summary.map(([name, count]) => (
									<Chip
										key={name}
										label={`${name}: ${count} รายการ`}
										className={classes.summaryChip}
										color="primary"
										variant="outlined"
										size="small"
									/>
								))}
							</Grid>

							<div className={classes.previewContainer}>
								{assignments.map((a, idx) => (
									<div key={a.sDocID} className={classes.previewRow}>
										<Typography variant="body2">
											{idx + 1}. {a.runningNumber} — {a.brandName}
										</Typography>
										<Chip
											label={a.adminName}
											size="small"
											color="primary"
										/>
									</div>
								))}
							</div>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<DialogActions style={{ padding: '12px 16px' }}>
				<Button onClick={handleClose} color="default" variant="outlined">
					ยกเลิก
				</Button>
				<Button
					onClick={handleConfirm}
					color="secondary"
					variant="contained"
					disabled={isLoadingAssignableRows || assignments.length === 0}
					startIcon={<AssignmentInd />}>
					ยืนยันมอบหมาย ({assignments.length} รายการ)
				</Button>
			</DialogActions>
		</Dialog>
	)
}

const Transition = forwardRef(function Transition(props, ref) {
	return <Slide direction="down" ref={ref} {...props} />
})
